import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
} from '@chakra-ui/react';

import { useNavigate } from 'react-router-dom';

import {
  ArrowLeftOutline,
  CloseCircleOutline,
  ExclamationCircleOutline,
  CheckOutline,
} from '@aslanpay/portal-common-lib';

import { useCancelAETFile } from 'app/hooks/useAETFileHooks';
import { useCancelEDFFile } from 'app/hooks/useEDFFileHooks';

import { FAILED_UPLOAD_STATUSES, FileUploadStatus } from 'app/constants/fileUploads';

import { useImportEmployeesStore } from 'app/store';

import { getEmployeesPath } from 'app/utils/routes';

import { LeaveWithoutImportModalProps } from './types';

function LeaveWithoutImportModal({
  isImporting,
  isOpen,
  onClose,
  onImport,
  onBack,
  hasChanges,
}: LeaveWithoutImportModalProps) {
  const navigate = useNavigate();

  const { importEmployeesData } = useImportEmployeesStore.getState();

  // Determine file type for context-aware cancel functionality
  const fileType = importEmployeesData.fileType || 'EMPLOYEE_DATA_FILE';
  const isAET = fileType === 'APPROVED_EARNINGS_TIMESHEET';

  // AET-specific cancel hook
  const { mutate: cancelAETFile, isLoading: isCancellingAETFile } = useCancelAETFile({
    onSuccess: () => {
      // Reset AET file state in store after successful cancellation
      const currentData = useImportEmployeesStore.getState().importEmployeesData;
      useImportEmployeesStore.getState().setImportEmployeesData({
        ...currentData,
        uploadedAETFile: undefined,
        bulkImportId: '',
        bulkImportStatus: undefined,
      });

      if (importEmployeesData.leaveModalSource === 'back') {
        onBack();
        onClose();
      } else {
        navigate(getEmployeesPath());
      }
    },
  });

  // EDF-specific cancel hook
  const { mutate: cancelEDFFile, isLoading: isCancellingEDFFile } = useCancelEDFFile({
    onSuccess: () => {
      // Reset EDF file state in store after successful cancellation
      const currentData = useImportEmployeesStore.getState().importEmployeesData;
      useImportEmployeesStore.getState().setImportEmployeesData({
        ...currentData,
        uploadedEDFFile: undefined,
        bulkImportId: '',
        bulkImportStatus: undefined,
      });

      if (importEmployeesData.leaveModalSource === 'back') {
        onBack();
        onClose();
      } else {
        navigate(getEmployeesPath());
      }
    },
  });

  const cancellationRequest = { statusReason: 'User cancelled the upload process' };

  const handleCancel = () => {
    if (isAET) {
      cancelAETFile({ fileUploadId: importEmployeesData.bulkImportId || '', cancellationRequest });
    } else {
      cancelEDFFile({ fileUploadId: importEmployeesData.bulkImportId || '', cancellationRequest });
    }
  };

  // Use the appropriate loading state
  const isLoading = isAET ? isCancellingAETFile : isCancellingEDFFile;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      closeOnEsc={false}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <ExclamationCircleOutline color="system.error" boxSize="24px" />
            <Text as="h6" color="brand.black">
              Leave without import
            </Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close leave without import"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Text color="brand.mutedPurple" textStyle="body">
            {hasChanges
              ? 'Are you sure you want to leave without importing your data file?'
              : 'No changes have been made to your employee data file so we cannot continue with the import.'}
          </Text>
        </ModalBody>
        <ModalFooter>
          {/* bulkImportStatus is set from real fps statuses (see UploadFile) — 'READY' is a
              legacy company-manager status that fps never emits, so this branch (the "Import"
              shortcut for a successfully-analysed upload) never actually rendered. */}
          {importEmployeesData.bulkImportStatus &&
          !FAILED_UPLOAD_STATUSES.includes(
            importEmployeesData.bulkImportStatus as FileUploadStatus
          ) ? (
            <>
              <Button
                mr="auto"
                size="sm"
                variant="outline"
                className="danger"
                onClick={handleCancel}
                isLoading={isLoading}
              >
                Yes, cancel
              </Button>
              <HStack justify="center" spacing="12px">
                <Button
                  size="sm"
                  rightIcon={<CheckOutline boxSize="20px" />}
                  onClick={() => onImport()}
                  isDisabled={!hasChanges}
                  isLoading={isImporting}
                >
                  Import
                </Button>
              </HStack>
            </>
          ) : (
            <HStack spacing="25px">
              <Button
                size="sm"
                variant="outline"
                onClick={onClose}
                leftIcon={<ArrowLeftOutline boxSize="25px" />}
              >
                Back
              </Button>
              <Button size="sm" onClick={handleCancel} isLoading={isLoading}>
                Yes, cancel
              </Button>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default LeaveWithoutImportModal;
